import React, { useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import Nav from "@/components/Nav";
import { LiveTiles } from "@/components/LiveTiles";
import { useAuth } from "@/context/AuthContext";
import { ArrowRight, Sparkles, Shield, Zap, Globe2, MessagesSquare, Languages, Check, Flag, Lock, ShieldCheck } from "lucide-react";

const HOW = [
  { icon: Sparkles, title: "Set your vibe", body: "Pick interests, languages, country preferences in seconds." },
  { icon: Zap, title: "Match instantly", body: "Our engine finds a live human match with shared interests." },
  { icon: MessagesSquare, title: "Talk. Skip. Repeat.", body: "Great chat? Stay. Want new? Skip to the next stranger." },
];

const FEATURES = [
  { icon: Globe2, title: "Instant Global Matching", body: "One tap connects you live with a real person from 190+ countries — matched on shared interests, language or country in seconds." },
  { icon: Languages, title: "Real-Time Translation", body: "Live captions translate what your match says as they speak, so language never blocks a great conversation." },
  { icon: Shield, title: "Secure AI Moderation", body: "AI vision and text moderation, one-tap reporting and instant blocking keep every call safe — with humans reviewing the hard cases." },
];

const TRUST = [
  { icon: Flag, title: "AI-Powered Reporting", body: "Every report is auto-triaged by AI with chat evidence attached, so moderators act in minutes, not days." },
  { icon: Lock, title: "End-to-End Signaling Safety", body: "Video flows peer-to-peer over encrypted WebRTC. Our servers only exchange connection signals — never your stream." },
  { icon: ShieldCheck, title: "Community Guidelines Enforced", body: "18+ only, verified emails, zero tolerance for nudity or harassment. Violations mean instant suspension." },
];

const FAQ = [
  { q: "Is Orbit Hello free?", a: "Yes. Start chatting for free. Meeting everyone is always free. Premium unlocks matching only men or only women, country filters, translation and priority matching." },
  { q: "Is my data private?", a: "We don't record video by default. You control what interests are visible." },
  { q: "How does matching work?", a: "We match on availability, language, country and shared interests — while keeping it random." },
  { q: "Can I report someone?", a: "Every call has a one-tap report button that ends the session and files a moderation review." },
];

export default function Landing() {
  const nav = useNavigate();
  const { user } = useAuth();

  const startChat = () => nav(user ? (user.onboarded ? "/match" : "/onboarding") : "/auth?tab=register");
  const tiltRef = useRef(null);
  const onTilt = (e) => {
    const el = tiltRef.current; if (!el) return;
    const r = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width - 0.5, y = (e.clientY - r.top) / r.height - 0.5;
    el.style.transform = `rotateY(${x * 10}deg) rotateX(${-y * 10}deg) translateZ(0)`;
  };
  const resetTilt = () => { if (tiltRef.current) tiltRef.current.style.transform = "rotateY(0deg) rotateX(0deg)"; };

  return (
    <div className="min-h-screen orbit-bg-grid">
      <Nav />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 pt-16 pb-24 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 orbit-fade-up">
            <span className="inline-flex items-center gap-2 orbit-chip active" data-testid="hero-live-badge">
              <span className="orbit-live-dot" /> 14,892 people online right now
            </span>
            <h1 className="mt-6 font-display text-5xl sm:text-6xl lg:text-7xl font-extrabold leading-[0.95] tracking-tight">
              Say <span className="orbit-gradient-text">hello</span>
              <br />
              to the world.
            </h1>
            <p className="mt-6 text-lg text-slate-300 max-w-xl">
              One tap, one new face. Orbit Hello connects you live with real people from 190+ countries — matched by interests, moderated 24/7, no downloads.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <button onClick={startChat} className="orbit-btn-primary inline-flex items-center gap-2 text-base" data-testid="hero-start-matching-btn">
                Start Video Chat <ArrowRight className="w-4 h-4" />
              </button>
              <a href="#how" className="orbit-btn-secondary inline-flex items-center gap-2 text-sm" data-testid="hero-how-link">
                How it works
              </a>
            </div>
            <div className="mt-10 flex items-center gap-5">
              <div className="flex -space-x-3">
                {[3, 6, 1, 8].map((n) => <img key={n} src={`/people/p${n}.jpg`} alt="" className="w-10 h-10 rounded-full border-2 border-[#0A0D14] object-cover" />)}
              </div>
              <div className="text-sm text-slate-400 leading-tight"><span className="text-slate-100 font-semibold">2.4M+ conversations</span> started this month<br /><span className="text-xs font-mono uppercase tracking-widest text-slate-500">No downloads · 190+ countries · AI moderated</span></div>
            </div>
          </div>

          {/* Preview card */}
          <div className="lg:col-span-5 orbit-fade-up" style={{ animationDelay: "120ms", perspective: "1200px" }} onMouseMove={onTilt} onMouseLeave={resetTilt}>
            <div ref={tiltRef} className="relative rounded-[28px] orbit-glass p-2.5 orbit-glow-cyan ring-1 ring-white/5 will-change-transform transition-transform duration-200 ease-out" data-testid="hero-board">
              <div className="relative aspect-[4/5] rounded-2xl overflow-hidden bg-slate-900">
                <LiveTiles className="absolute inset-0 grid-rows-2" />
                <div className="orbit-scanline" />
                <div className="absolute top-3 left-3 orbit-chip active !bg-emerald-500/10 !border-emerald-500/40 !text-emerald-300">● LIVE</div>
                <div className="absolute top-3 right-3 orbit-chip !text-slate-200 gap-1.5"><span className="inline-flex items-end gap-px h-3">{[1,2,3,4].map((b) => <span key={b} className="w-0.5 bg-cyan-400 rounded-sm" style={{ height: `${b * 25}%` }} />)}</span> 14ms</div>
                <div className="absolute bottom-3 left-3 right-3 orbit-glass rounded-xl p-3">
                  <div className="text-xs font-mono uppercase text-cyan-300 mb-1">AI Icebreaker</div>
                  <div className="text-sm text-slate-100">"If you could visit anywhere tomorrow, where would you go?"</div>
                </div>
                <div className="absolute bottom-3 right-3 w-20 h-24 rounded-lg overflow-hidden border border-white/20 bg-gradient-to-br from-fuchsia-500/40 to-cyan-500/40 backdrop-blur">
                  <div className="w-full h-full flex items-center justify-center text-xs text-white/80 font-mono">YOU</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="max-w-7xl mx-auto px-5 lg:px-8 py-24">
        <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
          <div>
            <div className="text-xs font-mono uppercase tracking-widest text-cyan-300">The flow</div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold mt-2">Three steps to your next friend</h2>
          </div>
        </div>
        <div className="relative grid md:grid-cols-3 gap-6">
          <div className="hidden md:block absolute top-9 left-[16%] right-[16%] h-px bg-gradient-to-r from-cyan-400/40 via-fuchsia-500/40 to-pink-500/40" />
          {HOW.map((h, i) => (
            <div key={h.title} className="relative orbit-glass rounded-2xl p-7 hover:border-cyan-500/40 hover:-translate-y-1 transition-[transform,border-color] duration-300" data-testid={`how-card-${i}`}>
              <div className="flex items-center justify-between">
                <span className="font-display text-5xl font-extrabold tracking-tight orbit-gradient-text">0{i + 1}</span>
                <div className="w-11 h-11 rounded-2xl bg-white/[0.04] border border-white/10 flex items-center justify-center"><h.icon className="w-5 h-5 text-cyan-300" /></div>
              </div>
              <div className="text-xl font-display font-semibold mt-5">{h.title}</div>
              <p className="text-slate-400 text-sm mt-2 leading-relaxed">{h.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURES */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-16">
        <div className="mb-10">
          <div className="text-xs font-mono uppercase tracking-widest text-cyan-300">Why Orbit Hello</div>
          <h2 className="font-display text-3xl sm:text-4xl font-bold mt-2 tracking-tight">Built for real conversations</h2>
        </div>
        <div className="grid md:grid-cols-12 gap-5">
          {FEATURES.map((f, i) => (
            <div key={f.title} className={`relative overflow-hidden orbit-glass rounded-3xl p-7 lg:p-9 hover:border-cyan-500/40 transition-colors ${i === 0 ? "md:col-span-8" : i === 1 ? "md:col-span-4" : "md:col-span-12"}`} data-testid={`feature-card-${i}`}>
              <div className={`absolute -right-16 -top-16 w-56 h-56 rounded-full blur-3xl ${i === 1 ? "bg-fuchsia-500/20" : i === 2 ? "bg-pink-500/10" : "bg-cyan-400/15"}`} />
              <div className="relative flex flex-col md:flex-row md:items-start gap-5">
                <div className="w-12 h-12 shrink-0 rounded-2xl bg-white/[0.04] border border-white/10 flex items-center justify-center"><f.icon className="w-6 h-6 text-cyan-300" /></div>
                <div className="max-w-2xl">
                  <div className="font-display text-xl lg:text-2xl font-semibold tracking-tight">{f.title}</div>
                  <p className="text-slate-400 text-base mt-2 leading-relaxed">{f.body}</p>
                </div>
                {i === 0 && (
                  <div className="md:ml-auto flex gap-2 shrink-0">
                    {[0, 5].map((n) => <img key={n} src={`/people/p${n}.jpg`} alt="" className="w-20 h-24 md:w-24 md:h-32 object-cover rounded-xl border border-white/10" />)}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* SAFETY */}
      <section id="safety" className="max-w-7xl mx-auto px-5 lg:px-8 py-24">
        <div className="orbit-glass rounded-3xl p-10 lg:p-14 relative overflow-hidden">
          <div className="absolute -top-20 -right-20 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl" />
          <div className="relative grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <div className="text-xs font-mono uppercase tracking-widest text-emerald-300 inline-flex items-center gap-2">
                <Shield className="w-3 h-3" /> Safety, by default
              </div>
              <h2 className="font-display text-3xl lg:text-4xl font-bold mt-3">You're in control. Always.</h2>
              <p className="text-slate-300 mt-4 max-w-lg">
                AI-assisted moderation, one-tap reporting, and hard bans for anyone crossing the line. Orbit Hello has zero tolerance for exploitation, harassment, or hate.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {["Age gate (18+)", "AI content review", "Report + auto-block", "Zero tolerance policy", "No video recording", "Human moderators"].map((t) => (
                <div key={t} className="flex items-center gap-2 text-sm text-slate-200"><Check className="w-4 h-4 text-cyan-300" />{t}</div>
              ))}
            </div>
          </div>
          <div className="relative grid md:grid-cols-3 gap-4 mt-10" data-testid="trust-badges">
            {TRUST.map((b, i) => (
              <div key={b.title} className="rounded-2xl border border-emerald-500/20 bg-emerald-500/5 p-5" data-testid={`trust-badge-${i}`}>
                <div className="inline-flex items-center gap-2 rounded-full bg-emerald-500/10 border border-emerald-500/30 px-3 py-1 text-[11px] font-mono uppercase tracking-wider text-emerald-300"><b.icon className="w-3.5 h-3.5" /> Verified</div>
                <div className="font-display font-semibold mt-3">{b.title}</div>
                <p className="text-slate-400 text-sm mt-1.5 leading-relaxed">{b.body}</p>
              </div>
            ))}
          </div>
          <div className="relative mt-6 text-xs text-slate-500 flex flex-wrap gap-x-4 gap-y-1">
            <Link to="/safety#guidelines" className="hover:text-cyan-300">Community Guidelines</Link>
            <Link to="/safety#terms" className="hover:text-cyan-300">Terms of Service</Link>
            <Link to="/safety#privacy" className="hover:text-cyan-300">Privacy Policy</Link>
            <Link to="/safety#cookies" className="hover:text-cyan-300">Cookie Policy</Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="max-w-3xl mx-auto px-5 lg:px-8 py-24">
        <h2 className="font-display text-3xl lg:text-4xl font-bold text-center">Questions, answered</h2>
        <div className="mt-10 space-y-3">
          {FAQ.map((f, i) => (
            <details key={f.q} className="orbit-glass rounded-xl p-5 group" data-testid={`faq-${i}`}>
              <summary className="cursor-pointer flex items-center justify-between font-display font-semibold text-slate-100">
                {f.q}<span className="text-cyan-300 group-open:rotate-45 transition">+</span>
              </summary>
              <p className="text-slate-400 text-sm mt-3">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-24">
        <div className="orbit-glass rounded-3xl p-10 lg:p-16 text-center relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 via-transparent to-fuchsia-500/5" />
          <h2 className="font-display text-4xl lg:text-5xl font-extrabold relative">Your next friend<br /><span className="orbit-gradient-text">is one click away.</span></h2>
          <button onClick={startChat} className="orbit-btn-primary mt-8 inline-flex items-center gap-2 relative" data-testid="cta-start-btn">
            Start Video Chat <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>

      <footer className="border-t border-white/5 py-10 text-center text-xs text-slate-500 font-mono">
        © 2026 Orbit Hello · orbithello.com · <Link to="/safety#guidelines" className="hover:text-cyan-300">Community Guidelines</Link> · <Link to="/safety#terms" className="hover:text-cyan-300">Terms</Link> · <Link to="/safety#privacy" className="hover:text-cyan-300">Privacy</Link> · <Link to="/safety#cookies" className="hover:text-cyan-300">Cookies</Link> · <Link to="/pricing" className="hover:text-cyan-300">Pricing</Link>
      </footer>
    </div>
  );
}
